import { Box, IconButton } from "@mui/material"
import { CancelFilledRounded, CheckCircleFilledRounded, VisibilityOutlined } from "@mui-symbols-material/w300"
import type { CellContext, ColumnDef } from "@tanstack/react-table"
import dayjs from "dayjs"
import type { ReactElement } from "react"
import React from "react"

import DisplayAS400Id from "@/components/common/DisplayAS400Id/DisplayAS400Id"
import { StatusIndicator } from "@/components/common/StatusIndicator/StatusIndicator"
import { CompanyCertificationType, CompanyStatus } from "@/graphql/codegen/graphql"
import type { CompanyCertification, CompanyListCertification } from "@/graphql/codegen/graphql"
import {
  CertificationExpirationsStatusColorMap,
  companyStatusMap,
} from "@/screens/Companies/components/tables/CetrificateExpirationsTableUtils"
import { CertificationStatus } from "@/screens/Companies/edit/components/certification/types"
import { getCertificationStatus } from "@/screens/Companies/edit/components/certification/utils"

const useCertificateExpirationsTableColumns = (): ColumnDef<CompanyListCertification>[] => {
  const renderCertificationIconByStatus = (certificationStatus: CertificationStatus) => {
    const statusColor = CertificationExpirationsStatusColorMap.get(certificationStatus)

    return (
      <Box className='ml-3 flex items-center justify-start gap-2 px-1'>
        {certificationStatus === CertificationStatus.MORE_THAN ? (
          <CheckCircleFilledRounded className={statusColor} />
        ) : (
          <CancelFilledRounded className={statusColor} />
        )}
      </Box>
    )
  }

  const renderCertificationIconByType = (
    certifications: CompanyCertification[],
    certificationType: CompanyCertificationType
  ) => {
    const filteredCertifications = certifications?.filter((el) => el?.certificationType === certificationType) ?? []

    if (!filteredCertifications?.length) {
      return renderCertificationIconByStatus(CertificationStatus.NOT_FILED)
    }

    const now = dayjs()

    const currentCerts = filteredCertifications
      ?.filter((cert) => {
        return dayjs(cert?.effectiveDate).isBefore(now)
      })
      .sort((a, b) => dayjs(b?.expirationDate).diff(dayjs(a?.expirationDate)))

    const status = getCertificationStatus(currentCerts[0]?.expirationDate as string)
    return renderCertificationIconByStatus(status)
  }

  return [
    {
      accessorKey: "name",
      header: "Company",
      sortingFn: "alphanumeric",
      cell: ({ row }: CellContext<CompanyListCertification, unknown>): ReactElement => {
        const company = row.original

        return (
          <Box className='ml-3 flex items-center justify-start gap-2 px-1'>
            <StatusIndicator status={company?.status ?? CompanyStatus.Inactive} map={companyStatusMap} />
            <div>{company?.name}</div>
          </Box>
        )
      },
      size: 300,
      enableSorting: true,
    },
    {
      accessorKey: "publicId",
      header: "Company ID",
      cell: ({ row }: CellContext<CompanyListCertification, unknown>): ReactElement => {
        const company = row.original

        return (
          <Box className='flex flex-col'>
            <Box className='flex items-center justify-start gap-2 px-1'>
              <StatusIndicator status={company?.status as CompanyStatus} map={companyStatusMap} />
              <div>{company?.publicId ?? "-"}</div>
            </Box>
            <Box>
              <DisplayAS400Id id={(company.externalSystemId ?? company.customerExternalSystemId) as string} />
            </Box>
          </Box>
        )
      },
      sortingFn: "alphanumeric",
      size: 200,
    },
    {
      accessorKey: "",
      header: "Ecovadis",
      cell: ({ row }: CellContext<CompanyListCertification, unknown>): ReactElement => {
        const certifications = (row.original?.certifications ?? []) as CompanyCertification[]
        return renderCertificationIconByType(certifications, CompanyCertificationType.Ecovadis)
      },
      size: 120,
      enableSorting: false,
    },
    {
      accessorKey: "",
      header: "ISO 9001",
      cell: ({ row }: CellContext<CompanyListCertification, unknown>): ReactElement => {
        const certifications = (row.original?.certifications ?? []) as CompanyCertification[]
        return renderCertificationIconByType(certifications, CompanyCertificationType.Iso_9001)
      },
      size: 120,
      enableSorting: false,
    },
    {
      accessorKey: "",
      header: "ISO 14001",
      cell: ({ row }: CellContext<CompanyListCertification, unknown>): ReactElement => {
        const certifications = (row.original?.certifications ?? []) as CompanyCertification[]
        return renderCertificationIconByType(certifications, CompanyCertificationType.Iso_14001)
      },
      size: 120,
      enableSorting: false,
    },
    {
      accessorKey: "",
      header: "ISO 27001",
      cell: ({ row }: CellContext<CompanyListCertification, unknown>): ReactElement => {
        const certifications = (row.original?.certifications ?? []) as CompanyCertification[]
        return renderCertificationIconByType(certifications, CompanyCertificationType.Iso_27001)
      },
      size: 120,
      enableSorting: false,
    },
    {
      accessorKey: "",
      header: "ISO 45001",
      cell: ({ row }: CellContext<CompanyListCertification, unknown>): ReactElement => {
        const certifications = (row.original?.certifications ?? []) as CompanyCertification[]
        return renderCertificationIconByType(certifications, CompanyCertificationType.Iso_45001)
      },
      size: 120,
      enableSorting: false,
    },
    {
      accessorKey: "",
      header: "ISO 50001",
      cell: ({ row }: CellContext<CompanyListCertification, unknown>): ReactElement => {
        const certifications = (row.original?.certifications ?? []) as CompanyCertification[]
        return renderCertificationIconByType(certifications, CompanyCertificationType.Iso_50001)
      },
      size: 120,
      enableSorting: false,
    },
    {
      accessorKey: "",
      header: "NACD",
      cell: ({ row }: CellContext<CompanyListCertification, unknown>): ReactElement => {
        const certifications = (row.original?.certifications ?? []) as CompanyCertification[]
        return renderCertificationIconByType(certifications, CompanyCertificationType.Nacd)
      },
      size: 120,
      enableSorting: false,
    },
    {
      accessorKey: "",
      header: "OSHA 45001",
      cell: ({ row }: CellContext<CompanyListCertification, unknown>): ReactElement => {
        const certifications = (row.original?.certifications ?? []) as CompanyCertification[]
        return renderCertificationIconByType(certifications, CompanyCertificationType.Osha_45001)
      },
      size: 120,
      enableSorting: false,
    },
    {
      accessorKey: "",
      header: "Customer Service Survey",
      cell: ({ row }: CellContext<CompanyListCertification, unknown>): ReactElement => {
        const certifications = (row.original?.certifications ?? []) as CompanyCertification[]
        return renderCertificationIconByType(certifications, CompanyCertificationType.CustomerServiceSurvey)
      },
      enableSorting: false,
    },
    {
      accessorKey: "actions",
      header: "",
      cell: () => (
        <IconButton>
          <VisibilityOutlined className='block text-gray-600' />
        </IconButton>
      ),
      size: 100,
      enableSorting: false,
    },
  ]
}
export default useCertificateExpirationsTableColumns
